import { useCallback, useRef } from "react"
import { X, UploadSimple, File as FileIcon, VideoCamera } from "@phosphor-icons/react"
import { cn } from "../../../lib/utils"

const ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/heic",
  "image/gif",
  "video/mp4",
  "video/quicktime",
  "video/webm",
]

const MAX_FILES = 5
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

interface TicketFileDropzoneProps {
  files: File[]
  onFilesChange: (files: File[]) => void
}

export function TicketFileDropzone({ files, onFilesChange }: TicketFileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const incoming = Array.from(newFiles).filter((f) => {
        if (!ACCEPTED_TYPES.includes(f.type)) return false
        if (f.size > MAX_FILE_SIZE) return false
        return true
      })
      const combined = [...files, ...incoming].slice(0, MAX_FILES)
      onFilesChange(combined)
    },
    [files, onFilesChange]
  )

  const removeFile = useCallback(
    (index: number) => {
      onFilesChange(files.filter((_, i) => i !== index))
    },
    [files, onFilesChange]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      addFiles(e.dataTransfer.files)
    },
    [addFiles]
  )

  const isImage = (type: string) => type.startsWith("image/")

  return (
    <div className="space-y-2">
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-border",
          "p-4 text-sm text-muted-foreground cursor-pointer transition-colors hover:border-primary/50 hover:bg-muted/50",
          files.length >= MAX_FILES && "opacity-50 pointer-events-none"
        )}
      >
        <UploadSimple className="h-5 w-5" />
        <span>Drop files here or click to browse</span>
        <span className="text-xs">Images & videos, max {MAX_FILES} files, 50MB each</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES.join(",")}
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files)
            e.target.value = ""
          }}
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
            >
              {isImage(file.type) ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="h-8 w-8 rounded object-cover flex-shrink-0"
                />
              ) : (
                <VideoCamera className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="flex-shrink-0 rounded-full p-1 hover:bg-muted transition-colors"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
